// Defensive wrapper around window.Telegram.WebApp.
// Every SDK call may throw "The operation is insecure" or similar in restricted
// webview contexts (some Telegram clients launch Mini Apps in sandboxed mode).
// We silently swallow these so a single missing capability does not break the app.

function safe<T>(fn: () => T, label: string): T | undefined {
  try {
    return fn();
  } catch (e) {
    console.warn(`[tg] ${label} failed:`, (e as Error).message);
    return undefined;
  }
}

export function tg(): WebApp {
  if (typeof window === "undefined" || !window.Telegram?.WebApp) {
    throw new Error("Telegram WebApp SDK not available");
  }
  return window.Telegram.WebApp;
}

export function isInTelegram(): boolean {
  return Boolean(window.Telegram?.WebApp?.initData);
}

export function ready(): void {
  if (!window.Telegram?.WebApp) return;
  const w = window.Telegram.WebApp;
  safe(() => w.ready(), "ready");
  safe(() => w.expand(), "expand");
  safe(() => w.setHeaderColor?.("secondary_bg_color"), "setHeaderColor");
  safe(() => w.disableVerticalSwipes?.(), "disableVerticalSwipes");
}

export function initData(): string {
  return safe(() => window.Telegram?.WebApp?.initData ?? "", "initData") ?? "";
}

export function startParam(): string | undefined {
  return safe(
    () => window.Telegram?.WebApp?.initDataUnsafe?.start_param,
    "startParam",
  );
}

export function setMainButton(text: string, onClick: () => void, opts?: { progress?: boolean; disabled?: boolean }): () => void {
  const w = window.Telegram?.WebApp;
  if (!w) return () => {};
  safe(() => {
    const btn = w.MainButton;
    btn.setText(text);
    if (opts?.disabled) btn.disable(); else btn.enable();
    if (opts?.progress) btn.showProgress(false); else btn.hideProgress();
    btn.show();
    btn.offClick(onClick);
    btn.onClick(onClick);
  }, "setMainButton");
  return () => safe(() => { w.MainButton.offClick(onClick); w.MainButton.hide(); }, "MainButton.cleanup") as unknown as void;
}

export function setBackButton(onClick: () => void): () => void {
  const w = window.Telegram?.WebApp;
  if (!w) return () => {};
  safe(() => { w.BackButton.onClick(onClick); w.BackButton.show(); }, "setBackButton");
  return () => safe(() => { w.BackButton.offClick(onClick); w.BackButton.hide(); }, "BackButton.cleanup") as unknown as void;
}

export function closeApp(): void {
  safe(() => window.Telegram?.WebApp?.close?.(), "close");
}

export function hapticImpact(style: "light" | "medium" | "heavy" = "light"): void {
  safe(() => window.Telegram?.WebApp?.HapticFeedback?.impactOccurred?.(style), "hapticImpact");
}

export function hapticNotification(type: "success" | "warning" | "error"): void {
  safe(() => window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred?.(type), "hapticNotification");
}
